import { TrashIcon, EyeIcon } from "@heroicons/react/outline";

import { NormalizedFile, UploadingFile } from "./types";

import { Button } from "@/components/ui/button";
import LoadingSpinner from "@/components/LoadingSpinner";

interface FilesListProps {
  isLoading: boolean;
  files: NormalizedFile[];
  uploadingFiles: UploadingFile[];
  emptyMessage: string;
  headerLabel: string;
  onDeleteFile: (file: NormalizedFile) => void;
  isFileDeleting: (fileId: string) => boolean;
  onPreviewFile?: (file: NormalizedFile) => void;
}

export const FilesList: React.FC<FilesListProps> = ({
  isLoading,
  files,
  uploadingFiles,
  emptyMessage,
  headerLabel,
  onDeleteFile,
  isFileDeleting,
  onPreviewFile,
}) => (
  <div>
    <h4 className="mb-2 text-sm font-semibold">{headerLabel}</h4>
    {(() => {
      if (isLoading) {
        return (
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size={6} />
          </div>
        );
      }

      if (files.length === 0 && uploadingFiles.length === 0) {
        return <div className="py-8 text-center text-sm text-gray-500">{emptyMessage}</div>;
      }

      return (
        <div className="space-y-2">
          {uploadingFiles.map(uploadingFile => (
            <div
              key={uploadingFile.tempId}
              className="flex items-center justify-between rounded border bg-gray-50 p-2"
            >
              <div className="flex-1">
                <p className="text-sm font-medium">{uploadingFile.name}</p>
                <p className="text-xs text-gray-500">{Math.round(uploadingFile.size / 1024)} KB</p>
              </div>
              <LoadingSpinner size={4} />
            </div>
          ))}
          {files.map(file => (
            <div
              key={file.id}
              className="flex items-center justify-between rounded border p-2 hover:bg-gray-50"
            >
              <div className="flex-1">
                <p className="text-sm font-medium">{file.displayName}</p>
                <p className="text-xs text-gray-500">
                  {file.sizeText}
                  {file.extraInfo ? ` \u00B7 ${file.extraInfo}` : ""}
                </p>
              </div>
              <div className="flex gap-2">
                {onPreviewFile && file.previewId && file.previewName && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => onPreviewFile(file)}
                    title="Preview file"
                  >
                    <EyeIcon className="h-4 w-4" />
                  </Button>
                )}
                <Button
                  size="sm"
                  variant="destructiveOutline"
                  onClick={() => onDeleteFile(file)}
                  disabled={isFileDeleting(file.id)}
                  title="Delete file"
                >
                  <TrashIcon className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      );
    })()}
  </div>
);
